import type { CrmTranslate } from './crm-copy';

export function createCrmCompaniesCopy(t: CrmTranslate) {
  return {
    companiesPage: {
      documentTitle: t('crm.companiesPage.documentTitle'),
      title: t('crm.companiesPage.title'),
      nouns: {
        company: t('crm.companiesPage.nouns.company'),
        companies: t('crm.companiesPage.nouns.companies'),
      },
      counts: {
        companies: (count: number) =>
          t('crm.companiesPage.counts.companies', {
            values: { count },
          }),
        contacts: (count: number) =>
          t('crm.companiesPage.counts.contacts', {
            values: { count },
          }),
        selected: (selected: number, total: number) =>
          t('crm.companiesPage.counts.selected', {
            values: { selected, total },
          }),
        showing: (visible: number, total: number) =>
          t('crm.companiesPage.counts.showing', {
            values: { visible, total },
          }),
        activeFilters: (count: number) =>
          t('crm.companiesPage.counts.activeFilters', {
            values: { count },
          }),
      },
      actions: {
        grid: t('crm.companiesPage.actions.grid'),
        list: t('crm.companiesPage.actions.list'),
        importCsv: t('crm.companiesPage.actions.importCsv'),
        addCompany: t('crm.companiesPage.actions.addCompany'),
        addCompanyNoPermission: t('crm.companiesPage.actions.addCompanyNoPermission'),
        clearSearch: t('crm.companiesPage.actions.clearSearch'),
        selectAllCompaniesTitle: t('crm.companiesPage.actions.selectAllCompaniesTitle'),
        unselectAllCompaniesTitle: t('crm.companiesPage.actions.unselectAllCompaniesTitle'),
        selectAll: t('crm.companiesPage.actions.selectAll'),
        unselectAll: t('crm.companiesPage.actions.unselectAll'),
        advancedFilters: t('crm.companiesPage.actions.advancedFilters'),
        removeSearchFilter: t('crm.companiesPage.actions.removeSearchFilter'),
        removeAdvancedFilter: (name: string) =>
          t('crm.companiesPage.actions.removeAdvancedFilter', {
            values: { name },
          }),
        clearAllFilters: t('crm.companiesPage.actions.clearAllFilters'),
        delete: t('crm.companiesPage.actions.delete'),
        deleting: t('crm.companiesPage.actions.deleting'),
        edit: t('crm.companiesPage.actions.edit'),
        undo: t('crm.companiesPage.actions.undo'),
        viewContacts: (count: number) =>
          t('crm.companiesPage.actions.viewContacts', {
            values: { count },
          }),
        viewContactsTitle: (count: number) =>
          t('crm.companiesPage.actions.viewContactsTitle', {
            values: { count },
          }),
        confirm: t('crm.companiesPage.actions.confirm'),
        cancel: t('crm.companiesPage.actions.cancel'),
      },
      search: {
        placeholder: t('crm.companiesPage.search.placeholder'),
      },
      sort: {
        label: t('crm.companiesPage.sort.label'),
        name: t('crm.companiesPage.sort.name'),
        industry: t('crm.companiesPage.sort.industry'),
        size: t('crm.companiesPage.sort.size'),
        created: t('crm.companiesPage.sort.created'),
        sortOrderTitle: (order: string) =>
          t('crm.companiesPage.sort.sortOrderTitle', {
            values: { order },
          }),
        ascending: t('crm.companiesPage.sort.ascending'),
        descending: t('crm.companiesPage.sort.descending'),
      },
      pageSize: {
        show: t('crm.companiesPage.pageSize.show'),
        perPage: t('crm.companiesPage.pageSize.perPage'),
      },
      filters: {
        activeFilters: t('crm.companiesPage.filters.activeFilters'),
        searchTag: (query: string) =>
          t('crm.companiesPage.filters.searchTag', {
            values: { query },
          }),
        advancedTag: (name: string, value: string) =>
          t('crm.companiesPage.filters.advancedTag', {
            values: { name, value },
          }),
        yes: t('crm.companiesPage.filters.yes'),
        no: t('crm.companiesPage.filters.no'),
      },
      advancedFilterLabels: {
        industry: t('crm.companiesPage.advancedFilterLabels.industry'),
        size: t('crm.companiesPage.advancedFilterLabels.size'),
        city: t('crm.companiesPage.advancedFilterLabels.city'),
        state: t('crm.companiesPage.advancedFilterLabels.state'),
        country: t('crm.companiesPage.advancedFilterLabels.country'),
        has_website: t('crm.companiesPage.advancedFilterLabels.hasWebsite'),
        has_email: t('crm.companiesPage.advancedFilterLabels.hasEmail'),
        has_phone: t('crm.companiesPage.advancedFilterLabels.hasPhone'),
        created_after: t('crm.companiesPage.advancedFilterLabels.createdAfter'),
        created_before: t('crm.companiesPage.advancedFilterLabels.createdBefore'),
        updated_after: t('crm.companiesPage.advancedFilterLabels.updatedAfter'),
        updated_before: t('crm.companiesPage.advancedFilterLabels.updatedBefore'),
        tags: t('crm.companiesPage.advancedFilterLabels.tags'),
      },
      states: {
        errorTitle: t('crm.companiesPage.states.errorTitle'),
        errorDescription: t('crm.companiesPage.states.errorDescription'),
        emptyTitle: t('crm.companiesPage.states.emptyTitle'),
        emptySearchDescription: t('crm.companiesPage.states.emptySearchDescription'),
        emptyCreateDescription: t('crm.companiesPage.states.emptyCreateDescription'),
        listEmptyTitle: t('crm.companiesPage.states.listEmptyTitle'),
        listEmptySearchDescription: t('crm.companiesPage.states.listEmptySearchDescription'),
        listCreateTitle: t('crm.companiesPage.states.listCreateTitle'),
        listCreateDescription: t('crm.companiesPage.states.listCreateDescription'),
      },
      confirmations: {
        deleteSelectedTitle: t('crm.companiesPage.confirmations.deleteSelectedTitle'),
        deleteSelectedMessage: (count: number, noun: string) =>
          t('crm.companiesPage.confirmations.deleteSelectedMessage', {
            values: { count, noun },
          }),
      },
      messages: {
        deleted: (count: number, noun: string) =>
          t('crm.companiesPage.messages.deleted', {
            values: { count, noun },
          }),
        restored: (count: number, noun: string) =>
          t('crm.companiesPage.messages.restored', {
            values: { count, noun },
          }),
        restoreFailed: t('crm.companiesPage.messages.restoreFailed'),
        deleteFailed: (count: number, noun: string) =>
          t('crm.companiesPage.messages.deleteFailed', {
            values: { count, noun },
          }),
        deleteError: t('crm.companiesPage.messages.deleteError'),
      },
      formatters: {
        companySize: (size: string) =>
          t('crm.companiesPage.formatters.companySize', {
            values: { size },
          }),
      },
    },
  };
}
